<template>
  <div class="container">

    <!-- Balance Card -->
    <div class="balance-card">
      <div class="balance-header-row">
        <div class="balance-label">Баланс</div>
        <button @click="handlePaymentHistoryClick" class="history-btn">📋</button>
      </div>
      <div :class="['balance-amount', { 'skeleton': balance === null && !hasSessionData }]">
        {{ balance !== null ? `${balance} ₽` : '0 ₽' }}
      </div>
      <button class="balance-btn" @click="handleDepositClick" :disabled="isLoading">
        💳 Пополнить баланс
      </button>
    </div>

    <!-- Swipeable Tariff Notification -->
    <div
      v-if="shouldShowTariffNotification && !isNotificationDismissed"
      class="tariff-notification"
      ref="notificationRef"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      :style="{ transform: `translateX(${swipeOffset}px)`, opacity: notificationOpacity }"
    >
      <div class="notification-icon">⚠️</div>
      <div class="notification-content">
        <div class="notification-title">{{ notificationTitle }}</div>
        <div class="notification-text">{{ notificationText }}</div>
      </div>
      <button class="notification-close" @click="dismissNotification">✕</button>
    </div>

    <!-- Active Subscriptions -->
    <div class="card">
      <div class="card-header">
        <div class="card-title">Мои подписки</div>
      </div>

      <!-- Loading skeleton (только если нет данных в sessionStorage) -->
      <div v-if="isInitialLoading" class="sub-item skeleton" style="height: 180px;"></div>

      <template v-else>
        <!-- Free Tariff -->
        <div v-if="isFreeTariffAvailable" class="sub-item">
          <div class="sub-header">
            <div class="sub-name">{{ freeTariff.tariff_name }}</div>
            <div class="sub-badge">Активна</div>
          </div>
          <div class="sub-info">
            <div class="sub-info-item">🌍 Базовые страны</div>
            <div class="sub-info-item">♾️ Безлимит</div>
          </div>
          <div class="sub-progress">
            <div class="progress-bar-bg">
              <div
                class="progress-bar-fill"
                :class="trafficBarClass"
                :style="{ width: `${trafficPercentage}%` }"
              ></div>
            </div>
            <div class="progress-label">{{ freeTariff.tariff_left }}</div>
          </div>
          <button
            class="btn-small btn-primary"
            style="width: 100%;"
            @click="handleConnectionClick('FreeTariff')"
            :disabled="isLoading"
          >
            🔗 Подключить
          </button>
        </div>

        <!-- Paid Tariffs -->
        <div
          v-for="(tariff, index) in uniqueTariffs"
          :key="index"
          class="sub-item"
        >
          <div class="sub-header">
            <div class="sub-name">{{ tariff.name }}</div>
            <div class="sub-badge" :class="{ 'inactive': tariff.daysRemaining <= 0 }">
              {{ tariff.daysRemaining > 0 ? 'Активна' : 'Истекла' }}
            </div>
          </div>
          <div class="sub-info">
            <div class="sub-info-item">🌍 {{ getCountriesText(tariff.name) }}</div>
            <div class="sub-info-item">♾️ Безлимит</div>
            <div class="sub-info-item">🖥 {{ getDevicesText(tariff.name) }}</div>
          </div>
          <div v-if="tariff.daysRemaining > 0" class="sub-progress">
            <div class="progress-bar-bg">
              <div
                class="progress-bar-fill"
                :style="{ width: `${calculateProgress(tariff.daysRemaining)}%` }"
              ></div>
            </div>
            <div class="progress-label">{{ tariff.subscriptionEnd }}</div>
          </div>
          <div v-else class="sub-info">
            <div class="sub-info-item">⏱ {{ tariff.subscriptionEnd }}</div>
          </div>
          <button
            v-if="tariff.daysRemaining > 0"
            class="btn-small btn-primary"
            style="width: 100%;"
            @click="handleConnectionClick(tariff.name)"
            :disabled="isLoading"
          >
            🔗 Подключить
          </button>
        </div>

        <!-- Empty state (только если нет данных И не идёт загрузка) -->
        <div v-if="!isFreeTariffAvailable && uniqueTariffs.length === 0" class="empty-state">
          <div class="empty-icon">📦</div>
          <div class="empty-title">Нет активных подписок</div>
          <div class="empty-text">Выберите тариф и начните пользоваться VPN</div>
          <button class="btn-large" @click="handleBuySubscriptionClick" style="margin-top: 16px;">
            Купить подписку
          </button>
        </div>
      </template>
    </div>

    <!-- Referral Banner -->
    <div class="referral-banner" @click="handleReferralClick" :disabled="isLoading">
      <div class="referral-content">
        <div class="referral-icon">🎁</div>
        <div class="referral-text">
          <div class="referral-title">Получи 10 дней</div>
          <div class="referral-subtitle">за каждого друга!</div>
        </div>
      </div>
      <button class="referral-btn">Пригласить</button>
    </div>

    <!-- Tasks Banner -->
    <div class="tasks-banner" @click="handleTasksClick" :disabled="isLoading">
      <div class="tasks-content">
        <div class="tasks-icon">✨</div>
        <div class="tasks-text">
          <div class="tasks-title">Получай дни</div>
          <div class="tasks-subtitle">за выполнение заданий!</div>
        </div>
      </div>
      <button class="tasks-btn">Открыть</button>
    </div>

    <!-- NEW: Welcome Popup -->
    <WelcomePopup
      :is-visible="showWelcomePopup"
      @close="closeWelcomePopup"
      @open-tasks="openTasksFromPopup"
    />

    <div class="legal-links">
      <a href="https://tma.avevpn.com/privacy_policy_page.html" target="_blank" rel="noopener noreferrer">
        Политика конфиденциальности
      </a>
      <span class="separator">•</span>
      <a href="https://tma.avevpn.com/user_agreement_avevpn.html" target="_blank" rel="noopener noreferrer">
        Пользовательское соглашение
      </a>
    </div>

  </div>
</template>

<script setup>
import {ref, onMounted, computed, watch} from "vue";
import {useRouter} from "vue-router";
import {haptic, disableSwipes} from "@/utils/telegram";
import WelcomePopup from "@/components/WelcomePopup.vue";
import { fetchAndCacheUserInfo } from '@/services/userService';

const router = useRouter();

const balance = ref(null);
const freeTariff = ref(null);
const uniqueTariffs = ref([]);
const showWelcomePopup = ref(false);
const botName = ref("Prometei");

// NEW: флаг для определения первоначальной загрузки
const hasSessionData = ref(false);

// Notification swipe state
const isNotificationDismissed = ref(false);
const swipeOffset = ref(0);
const notificationOpacity = ref(1);
const touchStartX = ref(0);
const notificationRef = ref(null);

const isFreeTariffAvailable = computed(() => {
  return freeTariff.value && freeTariff.value.tariff_name;
});

const trafficPercentage = computed(() => {
  if (!freeTariff.value) return 0;

  const totalDays = 3;
  const totalHours = totalDays * 24;
  const tariffLeftString = freeTariff.value.tariff_left;

  const parts = tariffLeftString.split(' ');
  if (parts.length < 4) return 0;

  const daysLeft = parseInt(parts[0], 10) || 0;
  const hoursLeft = parseInt(parts[2], 10) || 0;

  const totalLeftHours = daysLeft * 24 + hoursLeft;
  let percentage = (totalLeftHours / totalHours) * 100;
  const buffer = Math.min(percentage * 0.1, 60);

  return Math.max(0, percentage - buffer);
});

const trafficBarClass = computed(() => {
  const daysLeft = parseInt(freeTariff.value?.tariff_left || 0);

  if (daysLeft <= 1) {
    return 'danger';
  } else if (daysLeft === 2) {
    return 'warning';
  } else {
    return 'success';
  }
});

const shouldShowTariffNotification = computed(() => {
  return uniqueTariffs.value.some(tariff => {
    const isExpired = tariff.daysRemaining <= 0;
    const isExpiringSoon = tariff.daysRemaining > 0 && tariff.daysRemaining <= 7;
    return isExpired || isExpiringSoon;
  });
});

const notificationTariff = computed(() => {
  const expired = uniqueTariffs.value.find(t => t.daysRemaining <= 0);
  if (expired) return expired;
  return uniqueTariffs.value.find(t => t.daysRemaining > 0 && t.daysRemaining <= 7);
});

const notificationTitle = computed(() => {
  if (!notificationTariff.value) return '';
  if (notificationTariff.value.daysRemaining <= 0) {
    return 'Тариф истёк';
  }
  return 'Тариф скоро истечёт';
});

const notificationText = computed(() => {
  if (!notificationTariff.value) return '';
  const tariffName = notificationTariff.value.name;
  if (notificationTariff.value.daysRemaining <= 0) {
    return `Тариф "${tariffName}" истёк. Продлите подписку`;
  }
  const days = notificationTariff.value.daysRemaining;
  const daysWord = getDaysWord(days);
  return `Тариф "${tariffName}" истекает через ${days} ${daysWord}`;
});

const getCountriesText = (tariffName) => {
  if (tariffName.includes('Basic')) return '4 страны';
  if (tariffName.includes('Medium')) return '8 стран';
  if (tariffName.includes('Premium')) return '12 стран';
  return 'Несколько стран';
};

const getDevicesText = (tariffName) => {
  if (tariffName.includes('Basic')) return '1 устройство';
  if (tariffName.includes('Medium')) return '2 устройства';
  if (tariffName.includes('Premium')) return '10 устройств';
  return '1 устройство';
};

const calculateProgress = (daysRemaining) => {
  const maxDays = 30;
  return Math.min(100, (daysRemaining / maxDays) * 100);
};

// Функция добавления эмодзи к названию тарифа
const addEmojiToTariff = (tariffName) => {
  if (tariffName.includes('Free')) return 'Free 🌱';
  if (tariffName.includes('Basic')) return 'Basic 🧵';
  if (tariffName.includes('Medium')) return 'Medium 🚀';
  if (tariffName.includes('Premium')) return 'Premium 💎';
  return tariffName;
};

// NEW: Загрузка данных из sessionStorage
const loadDataFromSession = () => {
  const sessionBalance = sessionStorage.getItem("balance");
  const sessionAllTariffs = sessionStorage.getItem("all_tariffs");

  if (sessionBalance !== null) {
    balance.value = parseFloat(sessionBalance);
    hasSessionData.value = true;
  }

  if (sessionAllTariffs) {
    try {
      const tariffsData = JSON.parse(sessionAllTariffs);
      const currentTime = Math.floor(Date.now() / 1000);
      const tempTariffs = [];

      // Загружаем paid тарифы
      ['basic', 'medium', 'premium'].forEach(type => {
        if (tariffsData[type]?.expiry) {
          const expiry = parseInt(tariffsData[type].expiry);
          const endDate = new Date(expiry * 1000);
          const timeDifference = endDate - new Date();
          const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

          const tariffName = type.charAt(0).toUpperCase() + type.slice(1);

          tempTariffs.push({
            name: addEmojiToTariff(tariffName), // ✅ Добавляем эмодзи
            expiry: expiry,
            subscription_link: tariffsData[type].link || '',
            daysRemaining: daysRemaining,
            subscriptionEnd: daysRemaining > 0
              ? `Осталось ${daysRemaining} ${getDaysWord(daysRemaining)}`
              : "Подписка истекла"
          });
        }
      });

      uniqueTariffs.value = tempTariffs;

      // Загружаем free тариф
      if (tariffsData.free?.expiry) {
        const freeExpiry = parseInt(tariffsData.free.expiry);
        const currentTime = Math.floor(Date.now() / 1000);
        const timeLeft = freeExpiry - currentTime;

        if (timeLeft > 0) {
          const daysLeft = Math.floor(timeLeft / 86400);
          const hoursLeft = Math.floor((timeLeft % 86400) / 3600);

          freeTariff.value = {
            tariff_name: "Free 🌱", // ✅ Добавляем эмодзи
            tariff_left: `${daysLeft} дней ${hoursLeft} часов`,
            subscription_link: tariffsData.free.link || ''
          };
        }
      }

      hasSessionData.value = true;
    } catch (error) {
      console.error("Ошибка при загрузке тарифов из sessionStorage:", error);
    }
  }
};

// ✅ Обновление данных в фоне (без показа скелетонов)
const refreshDataInBackground = async () => {
  try {
    await fetchAndCacheUserInfo(true);
    // После обновления sessionStorage перезагружаем данные
    loadDataFromSession();
  } catch (error) {
    console.error("Ошибка при обновлении данных в фоне:", error);
  }
};

const sendMetric = async (buttonName) => {
  try {
    const token = sessionStorage.getItem("jwtToken");
    if (!token) {
      console.error("JWT токен не найден для отправки метрики");
      return;
    }

    const eventname = `Home-${buttonName}-${botName.value}`;

    const response = await fetch('https://baaack.zabanishumresh.com/addevent', {
      method: 'POST',
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        eventname: eventname
      })
    });

    if (response.ok) {
      console.log(`Метрика отправлена: ${eventname}`);
    } else {
      console.error(`Ошибка отправки метрики: ${response.status}`);
    }
  } catch (error) {
    console.error("Ошибка при отправке метрики:", error);
  }
};

const goTo = async (route, buttonName) => {
  haptic.impact();

  if (buttonName) {
    await sendMetric(buttonName);
  }

  router.push(`/${route}`);
};

const handleDepositClick = async () => {
  await sendMetric('Deposit-btn');
  goTo('deposit');
};

const handlePaymentHistoryClick = async () => {
  await sendMetric('PaymentHistory-btn');
  goTo('payment-history');
};

const handleConnectionClick = async (tariffName) => {
  await sendMetric(`Connect-${tariffName}`);
  goTo('connection');
};

const handleBuySubscriptionClick = async () => {
  await sendMetric('BuySubscription');
  goTo('buyVPN');
};

const handleReferralClick = async () => {
  await sendMetric('Referral');
  goTo('referal');
};

const handleTasksClick = async () => {
  await sendMetric('Tasks');
  goTo('tasks');
};

const handleTouchStart = (e) => {
  touchStartX.value = e.touches[0].clientX;
};

const handleTouchMove = (e) => {
  const currentX = e.touches[0].clientX;
  const diff = currentX - touchStartX.value;

  if (diff < 0) {
    swipeOffset.value = diff;
    notificationOpacity.value = 1 + (diff / 300);
  }
};

const handleTouchEnd = () => {
  if (swipeOffset.value < -100) {
    dismissNotification();
  } else {
    swipeOffset.value = 0;
    notificationOpacity.value = 1;
  }
};

const dismissNotification = async () => {
  await sendMetric('DismissNotification');
  swipeOffset.value = -500;
  notificationOpacity.value = 0;
  setTimeout(() => {
    isNotificationDismissed.value = true;
  }, 300);
};

const closeWelcomePopup = async () => {
  showWelcomePopup.value = false;
  localStorage.setItem("welcomeShown", "true");
  await sendMetric('WelcomePopup-Closed');
};

const openTasksFromPopup = async () => {
  showWelcomePopup.value = false;
  localStorage.setItem("welcomeShown", "true");
  await sendMetric('WelcomePopup-OpenTasks');
  goTo('tasks');
};

function getDaysWord(days) {
  if (days % 10 === 1 && days % 100 !== 11) {
    return "День";
  } else if (
      [2, 3, 4].includes(days % 10) &&
      ![12, 13, 14].includes(days % 100)
  ) {
    return "Дня";
  } else {
    return "Дней";
  }
}

onMounted(async () => {
  disableSwipes();

  isNotificationDismissed.value = false;
  swipeOffset.value = 0;
  notificationOpacity.value = 1;

  // ✅ Мгновенно загружаем данные из sessionStorage
  loadDataFromSession();

  const isTelegramWebApp = window.Telegram && window.Telegram.WebApp;

  if (isTelegramWebApp) {
    const webApp = window.Telegram.WebApp;
    const initData = webApp.initData || "";

    if (initData === "") {
      router.push('/welcome');
      return;
    }

    const telegramId = webApp.initDataUnsafe.user?.id;
    webApp.setHeaderColor("#1a1a2e");

    sessionStorage.setItem("tg_id", telegramId);

    // ✅ Обновляем данные в фоне (пользователь не заметит)
    refreshDataInBackground();

    const welcomeShown = localStorage.getItem("welcomeShown");

    if (!welcomeShown) {
      showWelcomePopup.value = true;
      await sendMetric('WelcomePopup-Shown');
    }
  } else {
    router.push('/welcome');
  }
});
</script>

<style scoped>
/* ... все стили остаются без изменений ... */
.container {
  width: 95%;
  height: 85%;
  padding: 20px 10px 100px 10px;
  border-radius: 20px;
  overflow-y: auto;
}

/* Balance Card */
.balance-card {
  background: rgba(26, 31, 41, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3),
  0 0 0 0.5px rgba(255, 255, 255, 0.05) inset;
}

.balance-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.history-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.history-btn:active {
  transform: scale(0.9);
  background: rgba(255, 255, 255, 0.2);
}

.balance-label {
  font-size: 13px;
  opacity: 0.9;
  font-weight: 500;
}

.balance-amount {
  font-size: 42px;
  font-weight: 800;
  letter-spacing: -1px;
  margin-bottom: 4px;
}

.balance-btn {
  background: rgba(255, 107, 53, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 107, 53, 0.3);
  color: #FF8555;
  padding: 14px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.balance-btn:active {
  transform: scale(0.97);
  background: rgba(255, 107, 53, 0.25);
}

/* Tariff Notification (Swipeable) */
.tariff-notification {
  background: rgba(255, 152, 0, 0.15);
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(255, 152, 0, 0.15);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
  opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  touch-action: pan-y;
  position: relative;
  overflow: hidden;
}

.tariff-notification::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #fbbf24, #f59e0b);
}

.notification-icon {
  font-size: 24px;
  flex-shrink: 0;
  line-height: 1;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 700;
  color: #fbbf24;
  margin-bottom: 4px;
  line-height: 1.2;
}

.notification-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.4;
}

.notification-close {
  width: 24px;
  height: 24px;
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 50%;
  font-size: 14px;
  color: #fbbf24;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  line-height: 1;
}

.notification-close:active {
  transform: scale(0.9);
  background: rgba(255, 152, 0, 0.3);
}

/* Card */
.card {
  background: rgba(26, 31, 41, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3),
  0 0 0 0.5px rgba(255, 255, 255, 0.05) inset;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
}

/* Subscription Item */
.sub-item {
  background: rgba(36, 43, 56, 0.6);
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.sub-item:last-child {
  margin-bottom: 0;
}

.sub-item:active {
  transform: scale(0.98);
}

.sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.sub-name {
  font-size: 16px;
  font-weight: 700;
}

.sub-badge {
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.sub-badge.inactive {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.sub-info {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.sub-info-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sub-progress {
  margin-top: 12px;
  margin-bottom: 8px;
}

.progress-bar-bg {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #FF6B35, #F7931E);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-bar-fill.danger {
  background: linear-gradient(90deg, #ff0000, #ff4d4d);
}

.progress-bar-fill.warning {
  background: linear-gradient(90deg, #ff7f00, #f6a152);
}

.progress-bar-fill.success {
  background: linear-gradient(90deg, #67c289, #3d8a57);
}

.progress-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  text-align: right;
}

.btn-small {
  padding: 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-primary {
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  color: white;
}

.btn-small:active {
  transform: scale(0.95);
}

.btn-large {
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  color: white;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(255, 107, 53, 0.3);
}

.btn-large:active {
  transform: scale(0.98);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 20px;
}

/* Referral Banner */
.referral-banner {
  background: rgba(117, 216, 185, 0.15);
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border: 1px solid rgba(117, 216, 185, 0.3);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.referral-banner:active {
  transform: scale(0.98);
}

.referral-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.referral-icon {
  font-size: 32px;
}

.referral-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.referral-title {
  font-size: 16px;
  font-weight: 700;
  color: #75d8b9;
}

.referral-subtitle {
  font-size: 13px;
  color: rgba(117, 216, 185, 0.8);
}

.referral-btn {
  background: rgba(117, 216, 185, 0.2);
  color: #75d8b9;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid rgba(117, 216, 185, 0.3);
  cursor: pointer;
}

/* Tasks Banner */
.tasks-banner {
  background: rgba(36, 43, 56, 0.6);
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tasks-banner:active {
  transform: scale(0.98);
}

.tasks-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tasks-icon {
  font-size: 32px;
}

.tasks-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.tasks-title {
  font-size: 16px;
  font-weight: 700;
}

.tasks-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
}

.tasks-btn {
  background: rgba(255, 107, 53, 0.15);
  color: #FF8555;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid rgba(255, 107, 53, 0.3);
  cursor: pointer;
}

/* Legal Links */
.legal-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  margin-top: 20px;
  font-size: 11px;
  color: #888;
  flex-wrap: wrap;
}

.legal-links a {
  color: #888;
  text-decoration: none;
  transition: color 0.2s ease;
}

.legal-links a:hover {
  color: #75d8b9;
}

.legal-links .separator {
  color: #555;
}

/* Skeleton */
.skeleton {
  position: relative;
  overflow: hidden;
  background: #1f1f1f;
  border-radius: 15px;
  -webkit-mask-image: -webkit-radial-gradient(white, black);
}

.skeleton::after {
  content: "";
  border-radius: inherit;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0,
      rgba(255, 255, 255, 0.05) 20%,
      rgba(255, 255, 255, 0.1) 60%,
      rgba(255, 255, 255, 0)
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
</style>